import { useState } from "react";
// @mui
import {
  Button,
  TableRow,
  MenuItem,
  TableCell,
  IconButton,
  Typography,
} from "@mui/material";
// @types
import { IDirectDebit } from "../../../@types/directDebits";
// components
import Label from "../../../components/label";
import Iconify from "../../../components/iconify";
import MenuPopover from "../../../components/menu-popover";
import ConfirmDialog from "../../../components/confirm-dialog";
// utils
import { fCurrency } from "../../../utils/formatNumber";
import { fDate } from "../../../utils/formatTime";
// routes
import { PATH_DASHBOARD } from "../../../routes/paths";
import { useNavigate } from "react-router-dom";

// ----------------------------------------------------------------------

type Props = {
  row: IDirectDebit;
  onEditRow: VoidFunction;
  onViewRow: () => void; // ← Add this
  onDeleteRow: VoidFunction;
};

export default function DirectDebitsTableRow({
  row,
  onEditRow,
  onViewRow,
  onDeleteRow,
}: Props) {
  const {
    id,
    clientName,
    clientId,
    directDebitStatus,
    paymentAmount,
    nextPaymentDueDate,
    paymentStatus,
  } = row;

  const navigate = useNavigate();

  const [openConfirm, setOpenConfirm] = useState(false);

  const [openPopover, setOpenPopover] = useState<HTMLElement | null>(null);

  const handleOpenConfirm = () => {
    setOpenConfirm(true);
  };

  const handleCloseConfirm = () => {
    setOpenConfirm(false);
  };

  const handleOpenPopover = (event: React.MouseEvent<HTMLElement>) => {
    setOpenPopover(event.currentTarget);
  };

  const handleClosePopover = () => {
    setOpenPopover(null);
  };

  const isEditable = [
    "pending",
    "pending_customer_approval",
    "pending_bank_account",
    "pending_acceptance",
  ].includes(row.directDebitStatus?.toLowerCase());

  return (
    <>
      <TableRow hover>
        <TableCell sx={{ textAlign: "center" }}>
          <Typography variant="subtitle2" noWrap>
            {clientName}
          </Typography>
        </TableCell>

        <TableCell sx={{ textAlign: "center" }}>{clientId}</TableCell>

        <TableCell sx={{ textAlign: "center" }}>
          <Label
            variant="soft"
            color={
              (directDebitStatus === "Active" && "success") ||
              (directDebitStatus === "Pending" && "warning") ||
              (directDebitStatus === "Cancelled" && "error") ||
              "default"
            }>
            {directDebitStatus}
          </Label>
        </TableCell>

        <TableCell sx={{ textAlign: "center" }}>
          {fCurrency(paymentAmount)}
        </TableCell>

        <TableCell sx={{ textAlign: "center" }}>
          {fDate(nextPaymentDueDate)}
        </TableCell>

        <TableCell sx={{ textAlign: "center" }}>{paymentStatus}</TableCell>

        <TableCell align="right">
          <IconButton
            color={openPopover ? "primary" : "default"}
            onClick={handleOpenPopover}>
            <Iconify icon="eva:more-vertical-fill" />
          </IconButton>
        </TableCell>
      </TableRow>

      <MenuPopover
        open={openPopover}
        onClose={handleClosePopover}
        arrow="right-top"
        sx={{ width: 140 }}>
        <MenuItem
          onClick={() => {
            onViewRow();
            handleClosePopover();
          }}>
          <Iconify icon="eva:eye-fill" />
          View
        </MenuItem>

        {/* Edit only for pending statuses */}
        {isEditable && (
          <MenuItem
            onClick={() => {
              onEditRow();
              handleClosePopover();
            }}>
            <Iconify icon="eva:edit-fill" />
            Edit
          </MenuItem>
        )}

        <MenuItem
          onClick={() => {
            handleOpenConfirm();
            handleClosePopover();
          }}
          sx={{ color: "error.main" }}>
          <Iconify icon="eva:trash-2-outline" />
          Delete
        </MenuItem>
      </MenuPopover>

      <ConfirmDialog
        open={openConfirm}
        onClose={handleCloseConfirm}
        title="Delete"
        content="Are you sure want to delete?"
        action={
          <Button variant="contained" color="error" onClick={onDeleteRow}>
            Delete
          </Button>
        }
      />
    </>
  );
}
